import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { Mutations, Queries } from '@shared/api';
import { isLayoutComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

interface SaveViewLayoutOrderInput {
  workflowTemplateId: string;
  viewTemplateId: string;
  /** The visible fields, in order. Hidden fields stay out: the view template decides visibility. */
  componentIds: string[];
  /** The view's saved layout, rewritten in place when there is one. */
  layout?: API.ViewTemplateLayoutFragment | null;
}

/**
 * Writes a view's field order as its layout: one standalone item per field, flat, because a
 * collection view has no sections. A field keeps the item id it already had, so the rewrite
 * updates items rather than replacing them.
 */
export function useSaveViewLayoutOrder(): (input: SaveViewLayoutOrderInput) => Promise<void> {
  const apollo = useApolloClient();

  return useCallback(
    async ({ workflowTemplateId, viewTemplateId, componentIds, layout }) => {
      const itemIds = new Map(
        (layout?.items ?? [])
          .filter(isLayoutComponent)
          .map(item => [item.componentId, item.id] as const),
      );
      const items: API.ViewTemplateLayoutOneOf[] = componentIds.map(componentId => {
        const id = itemIds.get(componentId);
        return {
          single_component: { ...(id ? { id } : {}), componentId, type: 'SINGLE_COMPONENT' },
        };
      });
      const refetch = {
        refetchQueries: [
          {
            query: Queries.viewTemplate.getViewTemplateLayout,
            variables: { input: { workflowTemplateId, viewTemplateId } },
          },
        ],
        awaitRefetchQueries: true,
      };

      if (layout) {
        await apollo.mutate<
          API.UpdateViewTemplateLayoutMutation,
          API.UpdateViewTemplateLayoutMutationVariables
        >({
          mutation: Mutations.viewTemplate.updateViewTemplateLayout,
          variables: { input: { id: layout.id, items } },
          ...refetch,
        });
      } else {
        await apollo.mutate<
          API.CreateViewTemplateLayoutMutation,
          API.CreateViewTemplateLayoutMutationVariables
        >({
          mutation: Mutations.viewTemplate.createViewTemplateLayout,
          variables: { input: { workflowTemplateId, viewTemplateId, items } },
          ...refetch,
        });
      }
    },
    [apollo],
  );
}
